'use client';

import { formatNumber } from '@lichtblick/localization';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { TextCTA } from '@lichtblick/photon/atoms/text-cta/text-cta';
import { InfoXs } from '@lichtblick/photon/icons/symbols/info-xs';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { Modal } from '@lichtblick/photon/organisms/modal/modal';
import { useState } from 'react';

import { ModalContent } from '@/components/modals/modal-content';
import { ELECTRICITY_USAGE, PEOPLE_LIMIT, usageLimitElectricity } from '@/constants';

const ELECTRICITY_USAGE_MODAL_TITLE = 'So berechnet LichtBlick den Stromverbrauch';
const MODAL_ID = 'electricity-usage-modal';

type ElectricityUsageProps = {
  defaultPeople?: string;
  defaultValue?: string;
  verboseLabel?: boolean;
};

export const ElectricityUsage: React.FC<ElectricityUsageProps> = ({
  defaultPeople,
  defaultValue,
  verboseLabel = false,
}) => {
  const [people, setPeople] = useState(defaultPeople ? Number(defaultPeople) : 1);
  const [usage, setUsage] = useState(defaultValue ?? String(ELECTRICITY_USAGE[people - 1]));

  return (
    <>
      <Stepper
        data-testid="people"
        errorMessage={`Die Personenanzahl muss zwischen ${PEOPLE_LIMIT.min} und ${PEOPLE_LIMIT.max} liegen`}
        label="Personenanzahl"
        max={PEOPLE_LIMIT.max}
        min={PEOPLE_LIMIT.min}
        name="people"
        onChange={(input) => {
          if (input !== people) {
            setPeople(input);
            setUsage(String(ELECTRICITY_USAGE[Math.max(0, Math.min(input, 10) - 1)]));
          }
        }}
        required
        value={people}
      />

      <Textfield
        data-testid="text-electricityUsage"
        errorMessage={`Ein höherer Verbrauch als ${formatNumber(usageLimitElectricity.max)} kWh/Jahr ist nicht möglich.`}
        footer={
          <TextCTA
            ariaLabel="Information"
            as="button"
            icon={<InfoXs />}
            command="show-modal"
            commandFor={MODAL_ID}
            slim
            type="button"
            variant="thin"
          >
            So berechnet LichtBlick den Stromverbrauch
          </TextCTA>
        }
        inputMode="numeric"
        label={verboseLabel ? 'Dein Stromverbrauch' : 'Dein Verbrauch'}
        max={usageLimitElectricity.max}
        min={usageLimitElectricity.min}
        name="electricityUsage"
        onChange={(e) => {
          setUsage(e.currentTarget.value);
        }}
        required
        rightSlot="kWh/Jahr"
        type="number"
        value={usage}
      />

      <Modal
        id={MODAL_ID}
        footerContent={
          <TextCTA as="button" type="button" command="close" commandFor={MODAL_ID} data-testid="button-modal-close">
            Ok, verstanden
          </TextCTA>
        }
        title={ELECTRICITY_USAGE_MODAL_TITLE}
      >
        <ModalContent>
          <Headline renderAs={'h2'} size="s">
            {ELECTRICITY_USAGE_MODAL_TITLE}
          </Headline>
          <Text renderAs={'p'}>
            Natürlich wisst nur ihr genau, was ihr verbraucht und könnt es deswegen auch direkt eintragen. Aus über 20
            Jahren Erfahrung als Ökostromanbieter kann LichtBlick aber auch ziemlich gut schätzen: Im Schnitt braucht
            eine Person ca. 1.400 kWh. Bei 2 Personen kommt man auf 2.300 kWh – jede weitere Person erhöht den Verbrauch
            jährlich um 700 kWh. Diese Werte werden also verwendet, wenn ihr die Personenzahl im Haushalt angebt.
          </Text>
        </ModalContent>
      </Modal>
    </>
  );
};
